// Builds the same receipt shape as mapOrderToReceipt for a POS gift voucher
// sale, so it prints, previews and goes on WhatsApp through the normal bill
// renderers. Selling a voucher is not a supply under GST, so there is no tax:
// no HSN, no GST summary, and the bill number is the sale's own GVS series
// number — nothing is taken from the billing (invoice) series.

const asNumber = (value) => parseFloat(value || 0);

export const GIFT_VOUCHER_RECEIPT_TITLE = 'GIFT VOUCHER RECEIPT';
export const GIFT_VOUCHER_NOT_TAX_NOTE = 'Not a tax invoice. GST is not applicable on the sale of gift vouchers; it applies when a voucher is redeemed.';
const PENDING_NOTE = 'Vouchers will be issued after approval. Codes and PINs are sent by email.';
const ISSUED_NOTE = 'Voucher codes and PINs are sent by email only.';

export const mapGiftVoucherSaleToReceipt = (payload, template, { changeDue = 0 } = {}) => {
  const sale = payload?.data?.gift_voucher_sale || payload?.gift_voucher_sale || payload;
  if (!sale) return null;

  const items = (sale.lines || []).map((line, index) => {
    const faceValue = asNumber(line.face_value);
    return {
      index: index + 1,
      name: `${line.gift_voucher_type_name || 'Gift'} Voucher`,
      color: '',
      size: '',
      barcode: '',
      hsn: '',
      quantity: asNumber(line.quantity),
      rate: faceValue,
      amount: faceValue * asNumber(line.quantity),
      discountAmount: asNumber(line.discount_amount),
      discountLabel: sale.discount_reason || '',
      savingsMessage: null,
    };
  });

  const payments = (sale.payments || [])
    .filter((payment) => asNumber(payment.amount) > 0 && !payment.gift_voucher_sale_cancellation_id)
    .map((payment) => ({
      label: String(payment.payment_mode || '').trim() || 'Payment',
      amount: asNumber(payment.amount),
      referenceNumber: payment.reference_number || '',
    }));

  const pending = sale.status === 'awaiting_issue';
  const change = asNumber(changeDue);

  return {
    orderId: sale.id,
    billNumber: sale.number || sale.id,
    invoiceTitle: GIFT_VOUCHER_RECEIPT_TITLE,
    createdAt: sale.paid_at || sale.created_at,
    storeName: sale.store?.name || sale.store_name || sale.organization?.name || '',
    storeAddress: sale.store?.address || '',
    storePhone: sale.store?.phone_number || sale.organization?.phone_number || '',
    storeEmail: sale.store?.email || sale.organization?.email || '',
    organizationName: sale.organization?.name || '',
    gstin: sale.organization?.gstin || '',
    cashierName: sale.cashier_name || '',
    salespersonName: '',
    customerName: sale.buyer_name || '',
    customerMobile: sale.buyer_phone_number || '',
    customerAddress: '',
    subtotal: asNumber(sale.face_value_total),
    discount: asNumber(sale.discount_total),
    billSavingsMessage: null,
    billDiscountAmount: 0,
    billDiscountLabel: '',
    billPromotionDiscountAmount: 0,
    billPromotionDiscountLabel: '',
    totalAmount: asNumber(sale.amount_payable),
    roundingAdjustment: 0,
    loyaltyRedemption: 0,
    loyaltyPointsEarned: 0,
    loyaltyPointsBalance: null,
    // The API records tenders net of cash change; the receipt shows what was handed over.
    totalPaid: asNumber(sale.amount_paid) + change,
    changeDue: change,
    payments: change > 0 ? addChangeToCash(payments, change) : payments,
    items,
    gstSummary: [],
    hideHsn: true,
    terms: template?.terms || [],
    footerMessage: template?.footer_message || '',
    websiteHandle: template?.website_handle || '',
    instagramHandle: template?.instagram_handle || '',
    declarationText: `${GIFT_VOUCHER_NOT_TAX_NOTE} ${pending ? PENDING_NOTE : ISSUED_NOTE}`,
    logoUrl: template?.logo_url || '',
    showTotalPaidRow: template?.show_total_paid_row !== false,
    rateColumnLabel: template?.rate_column_label || 'Rate',
    amountColumnLabel: template?.amount_column_label || 'Amt',
    showRateColumn: template?.show_rate_column !== false,
    showAmountColumn: template?.show_amount_column !== false,
    itemAmountDecimalPlaces: template?.item_amount_decimal_places || 2,
    receiptFontSize: template?.receipt_font_size || 'normal',
  };
};

// Change comes out of the cash tender (only cash can exceed the payable).
function addChangeToCash(payments, change) {
  const i = payments.findIndex((p) => /cash/i.test(p.label));
  if (i < 0) return payments;
  return payments.map((p, ix) => (ix === i ? { ...p, amount: p.amount + change } : p));
}
